<script setup lang="ts">
    import { computed } from 'vue';
    import { formatNumericText } from '@cwd/Shared/utils/formatNumericText';
    import { type IssueStatistics } from './interfaces';

    const props = withDefaults(
        defineProps<{
            issueStatistics:
                | (Pick<IssueStatistics, 'SentCount' | 'FailedCount' | 'RecipientCount' | 'BounceCount' | 'OpenCount' | 'ClickCount'> & {
                      [key: string]: any;
                  })
                | null;
            loading?: boolean;
            showReceivedAsCount?: boolean;
            showExactCounts?: boolean;
            journeyNodeOverlay?: boolean;
        }>(),
        {
            loading: false,
        }
    );

    // ReceivedCount = SentCount - BounceCount (emails successfully delivered)
    const getReceivedCount = computed(() => {
        if (!props.issueStatistics) return 0;
        return props.issueStatistics.SentCount - props.issueStatistics.BounceCount;
    });

    // SentCount = number of emails successfully sent
    const getSentCount = computed(() => {
        if (!props.issueStatistics) return 0;
        return props.issueStatistics.SentCount;
    });

    // SentPercentage = SentCount / RecipientCount * 100
    const getSentPercentage = computed(() => {
        if (!props.issueStatistics) return 0;
        return props.issueStatistics.RecipientCount === 0 ? 0 : (getSentCount.value / props.issueStatistics.RecipientCount) * 100;
    });

    // ReceivedPercentage = ReceivedCount / SentCount * 100
    const getReceivedPercentage = computed(() => {
        if (!props.issueStatistics) return 0;
        return getSentCount.value === 0 ? 0 : (getReceivedCount.value / getSentCount.value) * 100;
    });

    // OpenPercentage = OpenCount / ReceivedCount * 100
    const getOpenPercentage = computed(() => {
        if (!props.issueStatistics) return 0;
        return getReceivedCount.value === 0 ? 0 : (getOpenCount.value / getReceivedCount.value) * 100;
    });

    const getOpenCount = computed(() => {
        if (!props.issueStatistics) return 0;
        return props.issueStatistics.OpenCount;
    });

    // ClickPercentage = ClickCount / ReceivedCount * 100
    const getClickPercentage = computed(() => {
        if (!props.issueStatistics) return 0;
        return getReceivedCount.value === 0 ? 0 : (props.issueStatistics.ClickCount / getReceivedCount.value) * 100;
    });

    const displaySentCount = computed(() => {
        return props.showExactCounts ? getSentCount.value : formatNumericText(getSentCount.value, { precision: 1, largeNumbers: true });
    });

    const displayReceivedCount = computed(() => {
        if (props.showReceivedAsCount) {
            return props.showExactCounts ? getReceivedCount.value : formatNumericText(getReceivedCount.value, { precision: 1, largeNumbers: true });
        }
        return formatNumericText(getReceivedPercentage.value, { precision: 0 }) + '%';
    });
</script>

<template>
    <div class="d-flex col-gap-lg flex-wrap" :class="{ 'ug-bars': !journeyNodeOverlay, 'node-overlay': journeyNodeOverlay }">
        <div>
            <div class="bar-label">
                {{ $Resources.SentLabel }}
                <span class="skeleton" :class="loading && 'loading'">
                    {{ displaySentCount }}
                </span>
            </div>
            <div class="bar">
                <span class="bar-value bg-sent" :style="{ width: getSentPercentage + '%' }"></span>
            </div>
        </div>

        <template v-if="!issueStatistics?.ResendToExistingRecipients">
            <div>
                <div class="bar-label">
                    {{ $Resources.ReceivedLabel }}
                    <span class="skeleton" :class="loading && 'loading'">{{ displayReceivedCount }}</span>
                </div>
                <div class="bar">
                    <span class="bar-value bg-received" :style="{ width: getReceivedPercentage + '%' }"></span>
                </div>
            </div>

            <div>
                <div class="bar-label">
                    {{ $Resources.OpenedLabel }}
                    <span class="skeleton" :class="loading && 'loading'">{{ formatNumericText(getOpenPercentage) }}%</span>
                </div>
                <div class="bar">
                    <span class="bar-value bg-opened" :style="{ width: getOpenPercentage + '%' }"></span>
                </div>
            </div>

            <div>
                <div class="bar-label">
                    {{ $Resources.ClickedLabel }}
                    <span class="skeleton" :class="loading && 'loading'">{{ formatNumericText(getClickPercentage) }}%</span>
                </div>
                <div class="bar">
                    <span class="bar-value bg-clicked" :style="{ width: getClickPercentage + '%' }"></span>
                </div>
            </div>
        </template>
    </div>
</template>

<style lang="css" scoped>
    .bar-label {
        min-width: 64px;
    }
</style>
